import React, { useEffect, useState } from 'react';
import { reportsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart3, TrendingUp, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrency } from '../contexts/CurrencyContext';
import { HelpDot } from '../components/HelpDot';

export default function Reports() {
  const currency = useCurrency();
  const [salesReport, setSalesReport] = useState(null);
  const [pipelineReport, setPipelineReport] = useState(null);
  const [ticketsReport, setTicketsReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      const [salesRes, pipelineRes, ticketsRes] = await Promise.all([
        reportsAPI.getSalesReport({}),
        reportsAPI.getPipelineReport(),
        reportsAPI.getTicketsReport({}),
      ]);
      setSalesReport(salesRes.data);
      setPipelineReport(pipelineRes.data);
      setTicketsReport(ticketsRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="reports-loading">
        <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <div className="h-4 w-24 bg-muted animate-pulse rounded"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 w-16 bg-muted animate-pulse rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-1.5">Reports <HelpDot guide="reveal-and-reports" label="Understanding your reports" /></h1>
        <p className="text-muted-foreground">Analytics and insights for your business</p>
      </div>

      <Tabs defaultValue="sales" className="space-y-4" data-testid="reports-tabs">
        <TabsList>
          <TabsTrigger value="sales" data-testid="tab-sales">Sales</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-tickets">Tickets</TabsTrigger>
        </TabsList>

        <TabsContent value="sales" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Card data-testid="sales-quotes-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Quotes</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{salesReport?.quotes?.total || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {currency.format(salesReport?.quotes?.value || 0)} total value
                </p>
              </CardContent>
            </Card>

            <Card data-testid="sales-conversion-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{salesReport?.quotes?.conversion_rate || 0}%</div>
                <p className="text-xs text-muted-foreground">
                  {salesReport?.quotes?.accepted || 0} accepted quotes
                </p>
              </CardContent>
            </Card>

            <Card data-testid="sales-revenue-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Revenue</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{currency.format(salesReport?.invoices?.revenue || 0)}</div>
                <p className="text-xs text-muted-foreground">
                  {currency.format(salesReport?.invoices?.outstanding || 0)} outstanding
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="pipeline" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {pipelineReport && Object.entries(pipelineReport).map(([status, data]) => (
              <Card key={status} data-testid={`pipeline-${status}-card`}>
                <CardHeader>
                  <CardTitle className="text-base capitalize">{status}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{data.count}</div>
                  <p className="text-xs text-muted-foreground">
                    {currency.format(data.value || 0)} value
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="tickets" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Card data-testid="tickets-total-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Tickets</CardTitle>
                <Ticket className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{ticketsReport?.total || 0}</div>
              </CardContent>
            </Card>

            {ticketsReport?.avg_resolution_hours && (
              <Card data-testid="tickets-resolution-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Resolution Time</CardTitle>
                  <Ticket className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{ticketsReport.avg_resolution_hours.toFixed(1)}h</div>
                </CardContent>
              </Card>
            )}
          </div>

          {ticketsReport?.by_status && (
            <Card data-testid="tickets-by-status-card">
              <CardHeader>
                <CardTitle>By Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(ticketsReport.by_status).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between">
                      <span className="text-sm capitalize">{status}</span>
                      <span className="text-sm font-bold">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
